'use client'

import ComponentError from './ComponentError'
import NoEntry from './NoEntry'
import { ComponentSpinner } from './Spinner'

interface RequestGateProps<T> {
    children: (data: T) => React.ReactNode | React.ReactNode[]
    data: T | undefined
    showLoading: boolean
    error: any
    retry: () => void
    showEmptyState?: boolean
    loadingComponent?: React.ReactNode
    errorComponent?: React.ReactNode
    emptyComponent?: React.ReactNode
    paginationDataKey?: string
    notFoundOnError?: boolean
}

export default function RequestGate<T>({
    children,
    data,
    showLoading,
    error,
    retry,
    showEmptyState,
    loadingComponent,
    errorComponent,
    emptyComponent,
    paginationDataKey,
    notFoundOnError
}: RequestGateProps<T>) {
    if (showLoading) {
        return loadingComponent || <ComponentSpinner layout="block" />
    }

    if (error) {
        if (notFoundOnError) {
            // Dynamically import notFound only when needed to avoid router dependency in widget context
            try {
                const { notFound } = require('next/navigation')
                try {
                    return notFound()
                } catch {
                    // If notFound() throws (router not mounted), return a 404-like component
                    return (
                        <div className="px-4 py-4">
                            <NoEntry text={'Seite nicht gefunden'} />
                        </div>
                    )
                }
            } catch {
                // If notFound import fails (widget context), return a 404-like component
                return (
                    <div className="px-4 py-4">
                        <NoEntry text={'Seite nicht gefunden'} />
                    </div>
                )
            }
        }
        return errorComponent || <ComponentError retry={retry} />
    }

    if (
        showEmptyState &&
        data !== undefined &&
        Array.isArray(paginationDataKey ? (data as any)[paginationDataKey] : data) &&
        (paginationDataKey ? ((data as any)[paginationDataKey] as T[]).length === 0 : (data as T[]).length === 0)
    ) {
        return (
            emptyComponent || (
                <div className="px-4 py-4">
                    <NoEntry text={'Keine Einträge vorhanden'} />
                </div>
            )
        )
    }

    if (!data) {
        return <></>
    }

    return children(data)
}
